import { Injectable } from '@angular/core';
import * as DDMKT_LIB from "@citi-gcg-173875/cbol-marketing-lib";
import { MppRedesignService } from '../mpp-redesign/mpp-redesign.service';
import { AppConfigService } from '@citi-gcg-173875/citi-utils';
import { OfferUtilService } from 'src/app/common/services';
import { MktContentService } from 'src/app/core/services';

@Injectable({
    providedIn: 'root'
})
export class PdpDataTransformService {
    aemImagePrefix = '';
    constructor(
        private _mppRedesignService: MppRedesignService,
        private _appConfigService: AppConfigService,
        private _offerUtilService: OfferUtilService,
        private _mktContentService: MktContentService,
    ) {
        this.aemImagePrefix = this._appConfigService.getAEMConfig()?.aemStaticAssetsUrl;
    }

    public transform(pdpData: any, pageName = ''): any[] {
        console.log(`>>> ${pageName}: Transform data`);
        if (!pdpData) return [];

        const pageDataObj = pdpData?.page?.item ?? {};

        const sectionOrder = pageDataObj?.pageInfo?.sectionOrderList;
        if (!sectionOrder) return [];

        const sectionHandlers: Record<string, (data: any, index?: number) => any> = {
            hero: (data) => this.getHeroBanner(data),
            accel: (data) => this.getAccels(data?.cardData?.accelerator),
            feature: (data, index) => this.getFeatureContent(data?.features[index]),
            rateFees: (data) => this.getRateFees(data?.ratesFees),
            contentGrid: (data, index) => this.getContentGridContent(data?.contentGrid[index]),
            cardBenefits: (data) => this.getPrimaryBenefits(data?.cardData?.benefits),
            drawer: (data) => this.getDrawerContent(data?.drawer),
            faqData: (data) => this.getFaqContent(data?.faqs),
            banner: (data) => this.getBottomBannerData(data),
            pricing: (data) => this.getPricingDetails(data),
            pdpNavBar: (data) => this.pdpNavBar(data?.navBar),
            stickyHeader: (data) => this.buildStickyHeader(data),
            stickyFooter: (data) => this.buildStickyHeader(data),
            familyHero: (data) => this.familyHero(data),
            familyCards: (data) => this.getFamilyCardsContent(data),
            familyCtaSticky: (data) => this.buildCtaSticky(data)
        };

        return sectionOrder.reduce((components: any[], section: any) => {
            const baseSectionName = section?.sectionName?.replace(/\d+$/, ''); // Remove numeric suffix
            const match = section?.sectionName?.match(/(\d+)$/); // Extract numeric index if present
            const index = match ? parseInt(match[1], 10) - 1 : undefined; // Convert to zero-based index

            const handler = sectionHandlers[baseSectionName];
            if (handler) {
                let finalData = handler(pageDataObj, index);
                finalData.data.idStr = `c${index || '0'}-`;
                components.push(finalData);
            }
            return components;
        }, []);
    }

    private getHeroBanner(pageDataObj: any): any {
        return {
            component: DDMKT_LIB?.HeroBannerComponent,
            data: {
                template: "PDPHero",
                heroBannerData: this.buildHeroBannerData(pageDataObj)
            }
        };
    }

    private buildHeroBannerData(pageDataObj: any): any {
        const srcSetImgs = pageDataObj?.heroBannerBackgroundImage?.map((img: string) => `${this.aemImagePrefix}${img}`);
        const logo = this._offerUtilService.buildAEMImgUrl(this.aemImagePrefix, pageDataObj?.heroBannerCitiLogo?._path);
        pageDataObj.cardData.section = 'Hero';
        return {
            eyebrow: pageDataObj?.cardData?.cardName?.plaintext,
            eyebrowHeaderLevel: "1",
            headerLevel: "2",
            heroHeading: pageDataObj?.cardData?.pdpHeadline?.plaintext,
            dualHeaderIconName: "007-plus",
            logo: logo,
            citiLogoAltText: pageDataObj?.citiLogoAltText ?? 'Citi and American Airlines AAdvantage Logo',
            backgroundImage: srcSetImgs?.[0]?.split(' ')?.[0],
            srcset: srcSetImgs?.join(', '),
            featureRestatement: this.buildHeroFeatureRestatement(pageDataObj)
        };
    }

    private buildHeroFeatureRestatement(pageDataObj: any): any {
        return {
            isHeroFeature: true,
            ctaData: this._mppRedesignService.getCtaData(pageDataObj?.cardData, "", "pdp"),
            cardName: pageDataObj?.cardData?.cardName?.plaintext,
            text: pageDataObj?.cardData?.pdpSubheadline?.plaintext,
            pricingSection: {
                PricingDetails: pageDataObj?.cardData?.offerDisclaimer?.plaintext
            },
            flag: this.buildFlagData(pageDataObj),
            ratesfees: {
                ratesListItem:
                    pageDataObj?.cardData?.highlightBar?.map((item: any) => {
                        return { text: item?.heading, description: item?.description?.plaintext }
                    })
            },
            cardArtData: this.buildCardArtData(pageDataObj.cardData?.cardArtData),
            legalLinks: this._mppRedesignService.getLegalLinks(pageDataObj?.cardData, "", "pdp")
        };
    }

    private buildCardArtData(cardArtData: any): any {
        return {
            ...cardArtData,
            tabIndex: -1, // PDP page card art is not clickable/accessible as per figma (decorative image)
            cardArtUrl: this._offerUtilService.buildAEMImgUrl(this.aemImagePrefix, cardArtData?.cardArtUrl?._path)
        };
    }

    private buildCardArtDataForFamily( cardArtData: any, pdpPageLink: string) {
        return {
            ...cardArtData,
            tabIndex: -1, // PDP page card art is  clickable/accessible
            cardArtUrl: this._offerUtilService.buildAEMImgUrl(this.aemImagePrefix, cardArtData?.cardArtUrl?._path),
            navigationUrl: pdpPageLink,
            placeHolder: 'pdpPageLink',
            linkPreventDefault: true,
        };
    }

    private buildFlagData(pageDataObj: any): any {
        return {
            varient: null,
            classList: "",
            template: "cc_flag_v2",
            flagData: {
                headerLevel: 2,
                active: !!pageDataObj?.cardData?.flag,
                title: pageDataObj?.cardData?.flag?.title?.plaintext,
                backgroundColor: pageDataObj?.cardData?.flag?.backgroundColor,
                textColor: pageDataObj?.cardData?.flag?.textColor
            }
        };
    }

    private getBottomBannerData(pageDataObj: any): any {
        pageDataObj.cardData.section = pageDataObj?.cardData?.pdpHeadline?.plaintext;
        const heroBanner = this.buildHeroFeatureRestatement(pageDataObj);
        const legalLinks = pageDataObj?.bannerBottomClassList?.includes('hide-legal-links') ? {} : heroBanner?.legalLinks;
        const pricingSection = pageDataObj?.bannerBottomDisclaimer ? {                
            active: true,
            PricingDetails: pageDataObj?.bannerBottomDisclaimer?.plaintext || ''
        } : {};
        return {
            component: DDMKT_LIB?.FeatureComponent,
            data: {
                template: pageDataObj?.bannerBottomTemplate,
                eyebrow: pageDataObj?.cardData?.cardName?.plaintext,
                headerName: pageDataObj?.cardData?.pdpHeadline?.plaintext,
                headerLevel: "2",
                classList: heroBanner?.classList,
                featureBgColor: heroBanner?.featureBgColor,
                contentBgColor: heroBanner?.contentBgColor ?? 'rgba(255, 255, 255, 0)',
                frameBgColor: heroBanner?.frameBgColor ?? '#333',
                inlineImgUrl: heroBanner?.cardArtData?.cardArtUrl,
                frameImgUrl: this._offerUtilService.buildAEMImgUrl(this.aemImagePrefix, pageDataObj?.bannerBottomBackgroundImage?._path),
                featureRestatement: { ...heroBanner, legalLinks: legalLinks, ratesfees: null, cardArtData: null, pricingSection: pricingSection, flag: null, isHeroFeature: false }
            }
        };
    }

    private getAccels(accelData: any): any {
        const acceleratorItems = accelData?.acceleratorItems?.map(item => ({
            ...item,
            heading: item?.heading?.plaintext ?? '',
            description: item?.description?.plaintext ?? '',
            iconUrl: item?.iconUrl ? this._offerUtilService.buildAEMImgUrl(this.aemImagePrefix, item?.iconUrl?._path) : null,
        })) ?? [];

        return {
            component: DDMKT_LIB?.AcceleratorComponent,
            data: {
                template: accelData?.template ?? 'bar',
                barBackgroundColor: accelData?.backgroundColor,
                acceleratorItems
            }
        };
    }

    private getFeatureContent(featureData: any): any {
        const srcSetImgs = featureData?.frameImageUrl?.map((img: string) => `${this.aemImagePrefix}${img}`);
        const featureImg = srcSetImgs?.join(', ');
        if (featureData.featureRestatement) featureData.featureRestatement.section = featureData?.headerName?.plaintext;
        return {
            component: DDMKT_LIB?.FeatureComponent,
            data: {
                template: featureData?.template,
                eyebrow: featureData?.eyebrow?.plaintext ?? '',
                headerName: featureData?.headerName?.plaintext ?? '',
                headerLevel: featureData?.headerLevel,
                isBoldTextHeader: featureData?.isBoldTextHeader || false,
                classList: featureData?.classList,
                featureBgUrl: this._offerUtilService.buildAEMImgUrl(this.aemImagePrefix, featureData?.featureBgUrl?._path),
                featureBgColor: featureData?.featureBgColor,
                contentBgColor: featureData?.contentBgColor ?? 'rgba(255, 255, 255, 1)',
                frameBgColor: featureData?.frameBgColor ?? 'rgba(255, 255, 255, 0)',
                inlineImgUrl: this._offerUtilService.buildAEMImgUrl(this.aemImagePrefix, featureData?.imageUrl?._path),
                frameImgUrl: featureImg,
                featureList: featureData?.featureList?.map(t => ({
                    benefitHeaderLevel: '3',
                    title: t?.title?.plaintext ?? '',
                    content: t?.content?.plaintext ?? ''
                })) ?? [],
                featureCards: this.buildFeatureCards(featureData?.featureCards) ?? [],
                featureCardsIconName: featureData?.featureCardsIconName || '257-chevron-right',
                featureRestatement: this.buildFeatureRestatement(featureData?.featureRestatement) ?? {},
                featureFamilyRedirect: featureData?.featureFamilyRedirect ?? {},
                featureAccels: featureData?.featureAccels ?? []
            }
        };
    }

    private getRateFees(rateFees: any): any {
        return {
            component: DDMKT_LIB?.RatesfeesContainerComponent,
            data: {
                ratesfees: {
                    ...rateFees,
                    header: rateFees?.header?.plaintext ?? '',
                    headerLevel: 3,
                    ratesListItem: rateFees?.ratesListItem?.map(item => ({
                        eyebrow: item?.eyebrow?.plaintext ?? '',
                        text: item?.description?.plaintext.split('||')[0] ?? '',
                        description: item?.description?.plaintext.split('||')[1] ?? ''
                    })) ?? []
                }
            }
        };
    }

    private getContentGridContent(contentGridData: any): any {
        return {
            component: DDMKT_LIB?.ContentGridComponent,
            data: {
                template: contentGridData?.template,
                gridHeading: contentGridData?.gridHeading?.plaintext ?? '',
                gridDescription: contentGridData?.gridDescription?.plaintext ?? '',
                gridSubHeading: contentGridData?.gridSubHeading?.plaintext ?? '',
                flag: contentGridData?.flag,
                classList: contentGridData?.classList,
                headerClassList: contentGridData?.headerClassList,
                gridItems: contentGridData?.gridItems?.map(item => ({
                    iconName: item?.iconName,
                    heading: item?.heading,
                    description: item?.description?.plaintext ?? '',
                    imageUrl: item?.logoUrl?._path ? this._offerUtilService.buildAEMImgUrl(this.aemImagePrefix, item?.logoUrl?._path) : null,
                    imageClassList: item?.imageClassList,
                    altText: item?.altText
                })) ?? []
            }
        };
    }

    private getPrimaryBenefits(benefits: any): any {
        const createBenefitItem = (item: any) => ({
            ...item,
            header: item?.header?.plaintext ?? '',
            headerLevel: item?.headerLevel || '3',
            description: item?.description?.plaintext ?? '',
            default: {
                backgroundStyle: this.buildBgStyle(item?.defaultBackgroundStyle)
            },
            active: {
                backgroundStyle: this.buildBgStyle(item?.activeBackgroundStyle)
            }
        });

        return {
            component: DDMKT_LIB?.PrimaryBenefitsComponent,
            data: {
                primaryBenefits: benefits?.map(createBenefitItem)
            }
        };
    }

    private getDrawerContent(drawerData: any): any {
        const mapBenefits = (benefits: any[]) =>
            benefits?.map(item => ({
                ...item,
                header: item?.header?.plaintext ?? '',
                headerLevel: item?.headerLevel || '3',
                description: item?.description?.plaintext ?? '',
                iconUrl: item?.iconUrl ? this._offerUtilService.buildAEMImgUrl(this.aemImagePrefix, item?.iconUrl?._path) : null
            })) ?? [];

        const mapFaqs = (faq: any[]) =>
            faq?.map(item => ({
                ...item,
                title: item?.title?.plaintext ?? '',
                content: item?.content?.plaintext ?? ''
            })) ?? [];

        return {
            component: DDMKT_LIB?.DrawerComponent,
            data: {
                drawerData: {
                    ...drawerData,
                    benefits: mapBenefits(drawerData?.benefits),
                    faqs: {
                        isDrawer: true,
                        panelHeaderLevel: 3,
                        backgroundColor: drawerData?.backgroundColor,
                        headerHoverColor: drawerData?.headerHoverColor,
                        faqData: mapFaqs(drawerData?.faq)
                    }
                }
            }
        };
    }

    private getFaqContent(faqData: any): any {
        return {
            component: DDMKT_LIB?.FaqComponent,
            data: {
                ...faqData,
                panelHeaderLevel: 3,
                headline: faqData?.headline,
                faqData: faqData?.faqData?.map(item => ({
                    ...item,
                    title: item?.title?.plaintext ?? '',
                    content: item?.content?.plaintext ?? ''
                })) ?? []
            }
        };
    }

    private getPricingDetails(data: any): any {
        if (!data) return;
        if (!!data.cardData) data.cardData.section = data?.pricingDetails?.pricingHeading?.plaintext.trim();
        return {
            component: DDMKT_LIB?.PDPPricingComponent,
            data: {
                pricingSection: {
                    active: data?.pricingDetails?.active,
                    PricingHeading: data?.pricingDetails?.pricingHeading?.plaintext,
                    PricingDetails: data?.pricingDetails?.pricingDetails?.plaintext
                },
                cardName: data?.cardData?.cardName?.plaintext,
                legalLinks: this._mppRedesignService.getLegalLinks(data?.cardData, "", "pdp")
            }
        };
    }

    private pdpNavBar(navbarData: any): any {
        return {
            component: DDMKT_LIB?.CategoryNavBarComponent,
            data: {
                template: "category_nav_v2",
                active: true,
                headline: {
                    headerText: navbarData?.pdpNavbarHeader?.plaintext ?? ''
                },
                categoryTabs: navbarData?.categoryTabs?.map(obj => ({
                    ...obj, iconName: this._mppRedesignService?.getIcon(obj.categoryIconName),
                    productList: this.filterActiveProducts(obj?.productList)
                })) ?? [],
            }
        };
    }

    private buildFeatureCards(featureCards: any): any {
        if (!featureCards || !Array.isArray(featureCards)) {
            return [];
        }
        
        return featureCards.map(card => {
            if (!card) return null;
            return {
                showCardArt: card.cardArtData?.showCardArt ?? false,
                cardFeatureSpotlight: card.cardFeatureSpotlight ?? '',
                linkTarget: card.linkTarget ?? '_self',
                cardArtUrl: card.cardArtData?.cardArtUrl?._path
                    ? this._offerUtilService.buildAEMImgUrl(this.aemImagePrefix, card.cardArtData.cardArtUrl._path)
                    : null,
                cardArtSize: '64x40',
                ariaLabel: '',
                classlist: card.cardArtData?.classlist ?? null,
                cardName: card.cardName?.plaintext ?? '',
                navigationUrl: card.navigationUrl ?? `/credit-cards/${card.contentId || ''}`,
                linkPreventDefault: card.linkPreventDefault ?? false
            };
        }).filter(card => card !== null);
    }    

    private buildFeatureRestatement(featureRestatement: any): any {
        if (!featureRestatement && !featureRestatement?.ctaData) return {};
        const ctaData = featureRestatement?.ctaData?.length > 0 ? { ctaData: { button: featureRestatement.ctaData }, section: featureRestatement.section } : {};
        return {
            ctaData: this._mppRedesignService.getCtaData(ctaData, "", "pdp"),
            text: featureRestatement.text.plaintext,
            legalLinks: (!!featureRestatement?.legalLinks) ? this._mppRedesignService.getLegalLinks(featureRestatement?.legalLinks, "", "pdp") : []
        }
    }

    private buildStickyHeader(pageDataObj: any): any {
        const stickyNav = { ...pageDataObj?.cardData, ...pageDataObj?.cardData?.pdpStickyNav };
        stickyNav.section = 'StickyNav';
        return {
            component: DDMKT_LIB?.FamilyCtaStickyComponent,
            data: {
                classList: stickyNav?.classList,
                template: stickyNav?.pdpStickyTemplate,
                cardFamily: {
                    cardName: stickyNav?.pdpStickyCardName?.plaintext || pageDataObj?.cardData?.cardName?.plaintext,
                    cardArtData: this.buildCardArtData(pageDataObj?.cardData?.cardArtData),
                    ctaData: this._mppRedesignService.getCtaData(stickyNav, "", "pdp"),
                    promoDesp: stickyNav?.pdpStickyPromoDescription,
                    pid: pageDataObj?.cardData?.cardPid,
                    errorMsg: stickyNav?.pdpErrorMessage,
                    inputLabel: stickyNav?.pdpStickyInputLabel,
                    placeholder: stickyNav?.pdpStickyPlaceholder?.replace('PID', pageDataObj?.cardData?.cardPid),
                    pricingSection: (!!stickyNav?.pricingSection?.pricingDetails?.plaintext ? ({
                        active: true,
                        PricingHeading: stickyNav?.pricingSection?.pricingHeading?.plaintext,
                        PricingDetails: stickyNav?.pricingSection?.pricingDetails?.plaintext
                    }) : null),
                    legalLinks: stickyNav?.legalLinks && this._mppRedesignService.getLegalLinks(stickyNav, "", "pdp") || null
                }
            }
        };
    }


    public filterActiveProducts(productList: any[]): string[] {
        const bannerConfigContent = this._mktContentService.get('Branding', 'copy', 'BannerConfig', undefined);
        const cardNameMap = JSON.parse(bannerConfigContent?.productType || '{}');
        const activePids = (Object.keys(cardNameMap) || []);
        return productList.filter(({ cardPid }) => activePids.includes(cardPid));
    }

    private buildBgStyle = (bgStyle: string): string => bgStyle.replaceAll('$$path$$', this.aemImagePrefix) || '';

    // family page data transformation - starts
    private familyHero(pageDataObj: any): any {
        return {
            component: DDMKT_LIB?.HeroBannerComponent,
            data: {
                template: "familyHero",
                classList: '',
                heroBannerData: {
                    ...pageDataObj?.heroBanner?.heroBannerData[0],
                    heroHeading: pageDataObj?.heroBanner?.heroBannerData[0]?.heroHeading?.plaintext,
                    backgroundImage: this.buildBgStyle(pageDataObj?.heroBanner?.heroBannerData[0]?.backgroundImage)
                }
            }
        };
    }

    private getFamilyCardsContent(pageDataObj: any): any {
        return {
            component: DDMKT_LIB?.FamilyCardsComponent,
            data: {
                headerLevel: '2',
                familyCardsHeader: pageDataObj?.familyCardsHeader?.plaintext,
                familyCardsDescription: pageDataObj?.familyCardsDescription?.plaintext,
                familyCardsItems: pageDataObj?.features?.map(item => ({
                    template: 'cc_module_family_cards', //item?.template,
                    cardDetail: this.getFamilyCardDetail(item?.featureCards?.[0]),
                    cardArtData: this.buildCardArtDataForFamily(item?.featureCards?.[0]?.cardArtData, item?.featureCards?.[0]?.cardMetadata?.pdpPageLink ),
                    legalLinks: this._mppRedesignService.getLegalLinks(item?.featureCards?.[0], "", "pdp"),
                    ctaData: this.getPDPFamilyCTA(item?.featureCards?.[0]),
                    cardInfo: this.getFamilyCardInfo(item?.featureCards?.[0]),
                    benefitLists: this.getFamilyBenefitlist(item?.featureList),
                })) ?? []
            }
        };
    }

    private getPDPFamilyCTA(cardObj: any, section: string = ''): any {
        if (!cardObj) return {};
        return this._mppRedesignService.getCtaData({ ...cardObj, section: section, "ctaData": !!cardObj?.familyCtas ? cardObj?.familyCtas : cardObj?.ctaData }, "", "pdp");
    }

    private getFamilyCardDetail(cardData: any): any {
        return {
            ...cardData?.cardMetadata,
            pid: cardData?.cardPid,
            contentId: cardData?.contentId,
            cardName: cardData?.cardName.plaintext,
            stackView: false,
            annualFee: cardData?.cardAnnualFee?.plaintext,
            cardDescription: cardData?.cardDescription?.plaintext,
            headerClassList: 'cds-brand-vis',
            headerLevel: 2,
            additionalInformationText: 'Additional Information +',
            pricingInfo: 'Important Pricing & Information +',
            pidPT: cardData?.cardPid,
            cardArtData: this.buildCardArtData(cardData?.cardArtData)
        }
    }

    private getFamilyCardInfo(cardData: any): any {
        return {
            headerName: cardData?.familySubheading?.plaintext?.split('||')?.[0],
            headerLevel: null,
            featureList: [
                {
                    title: cardData?.familySubheading?.plaintext?.split('||')?.[1],
                    content: cardData?.familyOfferDisclaimer?.plaintext
                }
            ]
        }
    }

    private getFamilyBenefitlist(benefits: any): any {
        return benefits?.map(item => {
            return {
                template: 'benefits_list',
                classList: item?.classList || '',
                header: item?.title?.plaintext?.split('||')?.[0],
                flag: {
                    template: 'cc_flag_v2',
                    classList: '',
                    varient: '',
                    flagData: {
                        active: !!item?.title?.plaintext?.split('||')?.[1],
                        title: item?.title?.plaintext?.split('||')?.[1],
                        backgroundColor: '#BCC5C9',
                        textColor: '#121314'
                    }
                },
                descriptionList: item?.content?.plaintext?.split('||')
            }
        });
    }

    private buildCtaSticky(pageDataObj: any): any {
        return {
            component: DDMKT_LIB?.FamilyCtaStickyComponent,
            data: 
            {
                template: pageDataObj?.stickyCta?.template || "family_cta_sticky",
                classList: pageDataObj?.stickyCta?.classList || "header-sticky family_cta_sticky",
                headline: pageDataObj?.stickyCta?.stickyHeader || "Find Your Card",
                active: true,
                cardFamily: pageDataObj?.features?.map((item) => ({
                    pid: item?.featureCards[0]?.cardPid,
                    cardName: item?.featureCards[0]?.cardName?.plaintext,
                    cardArtData: {
                        ...this.buildCardArtDataForFamily(item?.featureCards[0]?.cardArtData, item?.featureCards[0]?.cardMetadata.pdpPageLink),
                        "cardArtSize": "sm"
                    },
                    ctaData: this.getPDPFamilyCTA(item?.featureCards[0], 'sticky'),
                })) || [],
                
                // ------ Uncomment below if stickyCtas are to be used -------

                // cardFamily: pageDataObj?.stickyCta?.stickyCtas?.map((item, index) => ({
                //     pid: pageDataObj?.features?.[index]?.featureCards[0]?.cardPid,
                //     cardName: pageDataObj?.features?.[index]?.featureCards[0]?.cardName?.plaintext,
                //     cardArtData: {
                //         ...this.buildCardArtData(pageDataObj?.features?.[index]?.featureCards[0]?.cardArtData),
                //         "cardArtSize": "sm"
                //     },
                //     ctaData: { button: item?.button.map((cta: any) => ({
                //         ...cta, pid: pageDataObj?.features?.[index]?.featureCards[0]?.cardPid,
                //         section: 'sticky' })) }
                //     })) || [],
            }
        };
    }
    // family page data transformation - ends

}
